import { format } from 'date-fns';
import { AlertTriangle, Copy, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

interface SettingsTabProps {
  settings: {
    isDefault: boolean;
    sendSettings: {
      overridePaperSize: boolean;
      paperSize: 'a4' | 'a5' | 'letter';
      filenameFormat: string;
    };
    templateInfo: {
      type: string;
      company: string;
      created?: Date;
      lastModified?: Date;
      startingPoint: string;
    };
  };
  isNewTemplate: boolean;
  otherDefaultTemplate?: string;
  onSettingsChange: (settings: any) => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}

export function SettingsTab({
  settings,
  isNewTemplate,
  otherDefaultTemplate,
  onSettingsChange,
  onDuplicate,
  onDelete,
}: Readonly<SettingsTabProps>) {
  const [showDefaultConfirmation, setShowDefaultConfirmation] = useState(false);
  const [showDefaultWarning, setShowDefaultWarning] = useState(false);
  const [showVariableDrawer, setShowVariableDrawer] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleEnableDefault = () => {
    if (otherDefaultTemplate) {
      setShowDefaultConfirmation(true);
      return;
    }
    onSettingsChange({ ...settings, isDefault: true });
  };

  const handleDisableDefault = () => {
    setShowDefaultWarning(true);
    onSettingsChange({ ...settings, isDefault: false });
  };

  const handleDefaultOn = () => {
    handleEnableDefault();
  };

  const handleDefaultOff = () => {
    if (settings.isDefault) {
      handleDisableDefault();
    } else {
      onSettingsChange({ ...settings, isDefault: false });
    }
  };

  const confirmDefaultChange = () => {
    onSettingsChange({ ...settings, isDefault: true });
    setShowDefaultConfirmation(false);
  };

  const cancelDefaultChange = () => setShowDefaultConfirmation(false);

  const insertVariable = (variable: string) => {
    onSettingsChange({
      ...settings,
      sendSettings: {
        ...settings.sendSettings,
        filenameFormat: settings.sendSettings.filenameFormat + variable,
      },
    });
    setShowVariableDrawer(false);
  };

  const filenameVariables = [
    { name: 'Account Number', value: '<<accountNumber>>' },
    { name: 'Customer Name', value: '<<customerName>>' },
    { name: 'Statement Date', value: '<<statementDate>>' },
    { name: 'Company Name', value: '<<companyName>>' },
  ];

  return (
    <div className="space-y-4 p-4">
      {/* Panel 1: Template Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Template Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="default-template">Default template</Label>
              <p className="text-xs text-muted-foreground">
                The default template is used automatically when sending
                statements for {settings.templateInfo.company}
              </p>
            </div>
            <Switch
              id="default-template"
              checked={settings.isDefault}
              onCheckedChange={(checked) =>
                checked ? handleDefaultOn() : handleDefaultOff()
              }
            />
          </div>

          {showDefaultConfirmation && (
            <div className="bg-muted p-3 rounded-md space-y-2">
              <p className="text-xs text-muted-foreground">
                {otherDefaultTemplate} is currently the default for{' '}
                {settings.templateInfo.company}. Setting this as default will
                replace it.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={confirmDefaultChange}
                  className="text-xs text-primary hover:underline"
                >
                  Confirm
                </button>
                <button
                  onClick={cancelDefaultChange}
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {showDefaultWarning && settings.isDefault === false && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
              <p className="text-xs text-amber-800">
                This template is currently the default. Turning this off means
                no default template will be set for{' '}
                {settings.templateInfo.company}.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Panel 2: Send Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Send Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="override-paper">
                  Override paper size for sending
                </Label>
                <p className="text-xs text-muted-foreground">
                  By default the paper size set in the Layout tab is used.
                  Enable this to set a different size specifically for when the
                  document is sent as an attachment.
                </p>
              </div>
              <Switch
                id="override-paper"
                checked={settings.sendSettings.overridePaperSize}
                onCheckedChange={(checked) =>
                  onSettingsChange({
                    ...settings,
                    sendSettings: {
                      ...settings.sendSettings,
                      overridePaperSize: checked,
                    },
                  })
                }
              />
            </div>

            {settings.sendSettings.overridePaperSize && (
              <div className="space-y-2 pt-2">
                <Select
                  value={settings.sendSettings.paperSize}
                  onValueChange={(value) =>
                    onSettingsChange({
                      ...settings,
                      sendSettings: {
                        ...settings.sendSettings,
                        paperSize: value as any,
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a4">A4</SelectItem>
                    <SelectItem value="a5">A5</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This overrides the Layout tab paper size for sent documents
                  only. The builder preview always reflects the Layout tab
                  setting.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="filename">PDF filename</Label>
            <Input
              id="filename"
              value={settings.sendSettings.filenameFormat}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  sendSettings: {
                    ...settings.sendSettings,
                    filenameFormat: e.target.value,
                  },
                })
              }
              maxLength={100}
              placeholder="Statement_<<accountNumber>>_<<statementDate>>"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                This is the filename when the PDF is downloaded or sent as an
                attachment. Use variables to make it unique per customer.
              </p>
              <p className="text-xs text-muted-foreground">
                {settings.sendSettings.filenameFormat.length} / 100
              </p>
            </div>
            <button
              onClick={() => setShowVariableDrawer(!showVariableDrawer)}
              className="text-xs text-primary hover:underline"
            >
              Insert variable +
            </button>
            {showVariableDrawer && (
              <div className="bg-muted p-3 rounded-md space-y-2">
                <p className="text-xs font-medium">Filename Variables</p>
                <div className="grid grid-cols-2 gap-2">
                  {filenameVariables.map((variable) => (
                    <button
                      key={variable.value}
                      onClick={() => insertVariable(variable.value)}
                      className="text-xs bg-background hover:bg-accent p-2 rounded border text-left"
                    >
                      {variable.name}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  Spaces in variable values will be replaced with underscores in
                  the filename automatically.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Panel 4: Template Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">
            Template Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Template type:</span>
              <span>{settings.templateInfo.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Company:</span>
              <span>{settings.templateInfo.company}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span>
                {settings.templateInfo.created
                  ? format(settings.templateInfo.created, 'PPP')
                  : 'Not yet saved'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last modified:</span>
              <span>
                {settings.templateInfo.lastModified
                  ? format(settings.templateInfo.lastModified, 'PPP')
                  : 'Not yet saved'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Starting point:</span>
              <span>{settings.templateInfo.startingPoint}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Panel 5: Danger Zone */}
      {!isNewTemplate && (
        <Card className="border-destructive/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-destructive">
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Duplicate this template</Label>
              <Button
                variant="outline"
                className="w-full"
                onClick={onDuplicate}
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicate Template
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Delete this template</Label>
              <Button
                variant="outline"
                className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Template
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete this template? This cannot be
                undone.
              </p>
              {settings.isDefault && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-md flex gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    This is the default template for{' '}
                    {settings.templateInfo.company}. Deleting it means no
                    default template will be set.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
